import { limitsStatusContract2Document } from '../limits-contract2.ts';
import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import type { DemoFixtureIdentity } from 'tio-employee/demo/types';
import { demoCompanyKey } from 'tio-employee/demo/identity-fixtures';
import {
  getCurrentDemoDebtLevel,
  type DemoDebtLevel,
} from 'tio-employee/utils/career-pathways-demo-data';

/**
 * Fixtures for the employer student-loan CONTRIBUTIONS demo: an SLR-eligible
 * employee on a monthly contribution plan with ~6 months of paid contributions
 * (MOHELA + a historical Nelnet servicer, plus one paid external-benefit
 * usage), a resent payment for status variety, and one upcoming payment.
 *
 * What the SLR / contributions dashboard reads through the store + request
 * manager:
 *  - `GET /api/v2/employee-payment-histories` (the kit endpoint, via the
 *    employeePaymentHistoryQuery builder → fetch seam) → the payment timeline.
 *    The normalizer reads `amount` (cents), `status`, `paymentDate`,
 *    `servicer`, and `benefitPeriod`; the fixture speaks the dasherized wire.
 *  - `findRecord('employee', …, { include: 'plan,company' })` → the SLR plan
 *    (cadence !== 'NEVER' gates SLR eligibility) + company + company-setting.
 *  - raw fetch / adapter paths (seam B): `/accounting_preview` (future
 *    payments), `/people/{id}/slr-alerts` (account state + alerts), and
 *    `/api/v2/limits/status?product=SLR` (the SLR limits summary).
 *
 * These fixtures DRIVE the live SLR/contributions render: the
 * `reimagined-data.ts` contributions `isDemoMode` branches were retired (#8537),
 * so `fetchContributionHistory` / `fetchEmployerContributions` / `fetchSection127Info`
 * run their real paths against these matchers plus the `person` findRecord below.
 */

const SERVICER_MOHELA = 'MOHELA';
const SERVICER_NELNET = 'Nelnet';

// One row of GET /api/v2/employee-payment-histories, in the kit's wire shape:
// dasherized attribute keys, amount in CENTS, ids carrying the UNION's
// synthetic source prefix. No failed or canceled rows — the endpoint excludes
// them server-side, so a fixture carrying one would pin a wire that cannot
// occur.
function paymentHistoryRow(
  id: string,
  amountCents: number,
  paymentDate: string | null,
  period: string | null,
  servicer: string,
  state: string
): JsonApiResource {
  return {
    type: 'employee-payment-histories',
    id: `accounting-transaction-${id}`,
    attributes: {
      amount: amountCents,
      'payment-date': paymentDate,
      'benefit-period': period,
      'line-item-type': 'CONTRIBUTION',
      servicer,
      status: state,
    },
  };
}

const CONTRIBUTIONS: JsonApiResource[] = [
  paymentHistoryRow('demo-contribution-1', 12500, '2026-05-14', '2026-05', SERVICER_MOHELA, 'sent'),
  paymentHistoryRow('demo-contribution-2', 12500, '2026-04-14', '2026-04', SERVICER_MOHELA, 'sent'),
  paymentHistoryRow('demo-contribution-3', 12500, '2026-03-14', '2026-03', SERVICER_MOHELA, 'sent'),
  // a resent payment for status variety
  paymentHistoryRow(
    'demo-contribution-5',
    12500,
    '2026-02-28',
    '2026-02',
    SERVICER_MOHELA,
    'resent'
  ),
  // an older payment from a previous servicer
  paymentHistoryRow('demo-contribution-6', 10000, '2025-06-14', '2025-06', SERVICER_NELNET, 'sent'),
  // a paid SLR external-benefit usage — the UNION's second source
  {
    type: 'employee-payment-histories',
    id: 'external-benefit-usage-demo-1',
    attributes: {
      amount: 5000,
      'payment-date': '2025-05-01',
      'benefit-period': null,
      'line-item-type': 'EXTERNAL CONTRIBUTION',
      servicer: SERVICER_NELNET,
      status: 'PAID',
    },
  },
];

// Same name derivation as the identity-fixtures plan (which shares this id),
// so the plan name cannot drift between the two pushes.
function buildPlanResource(employerName: string): JsonApiResource {
  return {
    type: 'plan',
    id: 'demo-slr-plan-1',
    attributes: {
      name: `${employerName} Student Loan Repayment`,
      companyKey: demoCompanyKey(employerName),
      cadence: 'MONTHLY',
      annualLimit: 150000,
      lifetimeLimit: 1200000,
      currency: 'USD',
      // Keep in sync with the identity-fixtures plan (same id, both pushed):
      // inline tier schedule read by scheduledMonthlyContribution — demo
      // sessions never call the live /plans/:id/tiers route.
      tiers: [{ fromCycle: 1, amount: 12500, per: 'MONTHLY' }],
    },
  };
}

// company-setting drives the employer-match (SECURE 2.0) card via
// modules.syf.isEnabled; this contributions-focused scenario has it off.
const COMPANY_SETTING_RESOURCE: JsonApiResource = {
  type: 'company-setting',
  id: 'demo-slr-companysetting-1',
  attributes: {
    modules: { syf: { isEnabled: false } },
  },
};

// Employer/employee names derive from the shared fixture identity so the SLR
// surfaces always agree with the session identity (header/greeting).
function buildCompanyResource(employerName: string): JsonApiResource {
  return {
    type: 'company',
    id: 'demo-slr-company-1',
    attributes: { name: employerName, isActive: true },
    relationships: {
      companySetting: { data: { type: 'company-setting', id: 'demo-slr-companysetting-1' } },
    },
  };
}

// Employee findRecord (include plan,company). The dashboard reads
// employee.plan.cadence to decide SLR eligibility, so the plan is sideloaded.
// (Product fixture only — the demo session identity is owned by
// demo/identity-fixtures.ts buildDemoIdentityDoc, which reuses the TAS ids.)
function buildEmployeeResource(identity: DemoFixtureIdentity): JsonApiResource {
  return {
    type: 'employee',
    id: 'demo-slr-employee-1',
    attributes: { firstName: identity.firstName, lastName: identity.lastName, isActive: true },
    relationships: {
      plan: { data: { type: 'plan', id: 'demo-slr-plan-1' } },
      company: { data: { type: 'company', id: 'demo-slr-company-1' } },
    },
  };
}

// --- Person + linked account (drives the contributions account cards) -------
// The SLR/contributions routes do findRecord('person', personId, {
//   include: 'accounts.financial-institution,accounts.loans,accounts.postal-address' })
// and read person.activeAccounts / person.accountReceivingContributions. All of
// account's relationships (financial-institution / loans / postal-address) are
// async:false, so they must be sideloaded. `accountReceivingContributionsId` is
// a plain attribute and must point at a seeded ACTIVE account. The handler
// echoes the requested (real-session) person id, so this fixture id is arbitrary.
const POSTAL_ADDRESS_RESOURCE: JsonApiResource = {
  type: 'postal-address',
  id: 'demo-slr-addr-1',
  attributes: {
    addressLine1: '633 Spirit Drive',
    city: 'Chesterfield',
    state: 'MO',
    postalCode: '63005',
    postalAddressType: 'PAYMENT',
    isActive: true,
  },
};

const FINANCIAL_INSTITUTION_RESOURCE: JsonApiResource = {
  type: 'financial-institution',
  id: 'demo-slr-fi-1',
  attributes: { name: SERVICER_MOHELA, isActive: true },
};

// UNITS FIX (was `balance: 4500000` commented as cents): the wire truth is
// DOLLARS — tio-api serializes loan `observation.balance` through
// `convert_to_currency_float` (loan.rb), and `LoanModel.currentBalance`
// returns it raw. The old cents value rendered $4,500,000 on any
// currentBalance consumer (the shipped dashboard's loan-summary tile). Driven
// by the debt axis so every SLR surface agrees with the selected level.
function buildLoanResource(debt: DemoDebtLevel): JsonApiResource {
  return {
    type: 'loan',
    id: 'demo-slr-loan-1',
    attributes: {
      isActive: true,
      observation: { balance: debt.balanceDollars ?? 0, 'next-payment-date': '2026-07-01' },
    },
    relationships: {
      account: { data: { type: 'account', id: 'demo-slr-account-1' } },
    },
  };
}

function buildAccountResource(debt: DemoDebtLevel): JsonApiResource {
  return {
    type: 'account',
    id: 'demo-slr-account-1',
    attributes: {
      status: 'ACTIVE',
      isActive: true,
      number: '****4321',
      publicOrdinalValue: 1,
      observation: {
        // account-level balance is DOLLARS (AccountModel.loanTotal returns it
        // raw, no /100).
        balance: debt.balanceDollars ?? 0,
        'interest-weighted-average': debt.interestRate ?? 5.5,
        // minimum-monthly-payment IS cents (loanMinMonthlyPayment divides by 100).
        'minimum-monthly-payment': debt.monthlyPaymentCents ?? 0,
        'payable-account-number': '****4321',
        'provider-success-at': '2026-05-01T00:00:00Z',
        'is-paid-off': false,
      },
      summary: { loanCount: 1 },
    },
    relationships: {
      financialInstitution: { data: { type: 'financial-institution', id: 'demo-slr-fi-1' } },
      loans: { data: [{ type: 'loan', id: 'demo-slr-loan-1' }] },
      postalAddress: { data: { type: 'postal-address', id: 'demo-slr-addr-1' } },
      person: { data: { type: 'person', id: 'demo-slr-person-1' } },
    },
  };
}

function buildPersonResource(identity: DemoFixtureIdentity, debt: DemoDebtLevel): JsonApiResource {
  // `not-linked` keeps this person account-less too — consistent with the
  // identity person fixtures, so the SLR surfaces show the real "add loan"
  // empty state instead of a phantom $0 linked account.
  const hasLoans = debt.hasLinkedLoans;
  return {
    type: 'person',
    id: 'demo-slr-person-1',
    attributes: {
      firstName: identity.firstName,
      lastName: identity.lastName,
      ...(hasLoans ? { accountReceivingContributionsId: 'demo-slr-account-1' } : {}),
    },
    relationships: {
      accounts: { data: hasLoans ? [{ type: 'account', id: 'demo-slr-account-1' }] : [] },
    },
  };
}

function servicerBreakdown(rows: JsonApiResource[]): { servicer: string; total: number }[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const attrs = row.attributes as { servicer: string; amount: number };
    totals.set(attrs.servicer, (totals.get(attrs.servicer) ?? 0) + attrs.amount);
  }
  return [...totals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([servicer, total]) => ({ servicer, total }));
}

const contributionsCollection: JsonApiDoc = {
  data: CONTRIBUTIONS,
  meta: {
    page: { total: CONTRIBUTIONS.length, number: 1, size: 250 },
    summary: {
      total: CONTRIBUTIONS.reduce(
        (sum, row) => sum + (row.attributes as { amount: number }).amount,
        0
      ),
      'servicer-breakdown': servicerBreakdown(CONTRIBUTIONS),
    },
  },
};

export function buildContributionsMatchers(identity: DemoFixtureIdentity): MatcherFixture[] {
  const debt = getCurrentDemoDebtLevel();
  const employeeDoc: JsonApiDoc = {
    data: buildEmployeeResource(identity),
    included: [
      buildPlanResource(identity.employerName),
      buildCompanyResource(identity.employerName),
      COMPANY_SETTING_RESOURCE,
    ],
  };
  const personDoc: JsonApiDoc = {
    data: buildPersonResource(identity, debt),
    included: debt.hasLinkedLoans
      ? [
          buildAccountResource(debt),
          FINANCIAL_INSTITUTION_RESOURCE,
          buildLoanResource(debt),
          POSTAL_ADDRESS_RESOURCE,
        ]
      : [],
  };
  return [
    { matcher: { kind: 'op', op: 'findRecord', type: 'employee' }, docs: [employeeDoc] },
    { matcher: { kind: 'op', op: 'findRecord', type: 'person' }, docs: [personDoc] },
  ];
}

// --- Raw-fetch (seam B) matchers -------------------------------------------
// The contributions dashboard reaches these directly (bypassing the store):
//  - SLR limits summary (getV2SlrLimitsStatus → raw window.fetch) and SLR alerts
//    (api.call → raw window.fetch) are caught by the DemoModeService fetch
//    interceptor — no gating code needed.
//  - The future-payments (/accounting_preview) and past-payments
//    (/accounting_payments) endpoints go through the employee adapter's `ajax`,
//    which bypasses the request-manager demo handler, so the EmployeeAdapter
//    consults `demoJsonFor` for them (the tas-participant eligibility pattern).

// /employees/{id}/accounting_preview → upcoming contribution(s). amount in CENTS.
const FUTURE_PAYMENTS = [{ amount: 12500, period: '2026-06', validation_errors: [] }];

// GET /people/{id}/slr-alerts → page state + account status + alerts.
function buildSlrAlerts(debt: DemoDebtLevel) {
  // The kit's document shape (api/v2/people/:id/slr-alerts): parseSlrAlerts reads
  // data.attributes with dasherized keys, so a flat v1-shaped fixture would parse
  // to an empty report and silently hide whatever state the demo meant to show.
  if (!debt.hasLinkedLoans) {
    // The endpoint's page-state vocabulary has no "no-accounts" value — the
    // coherent no-linked-loan state on this wire is `non-slr-never`
    // (contribution-history renders its empty-state banner for it; the
    // identity-level catch-all uses the same value).
    return {
      data: {
        attributes: { 'page-state': 'non-slr-never', alerts: [], 'contribution-account': null },
      },
    };
  }

  return {
    data: {
      attributes: {
        'page-state': 'active',
        alerts: [],
        'contribution-account': {
          id: 'demo-slr-account-1',
          'servicer-name': SERVICER_MOHELA,
          'account-number': '****4321',
          // This raw endpoint's balance IS cents (its consumer divides by 100).
          balance: debt.balanceCents ?? 0,
          'is-pif': false,
          'servicer-address-type': null,
          status: 'ACTIVE',
        },
      },
    },
  };
}

// GET /api/v2/limits/status?product=SLR → the contract-2 document
// parseLimitStatuses maps back into the app shape. $1,500 annual / $12,000
// lifetime, $700 remaining. Values in cents.
const SLR_ANNUAL_LIMIT = 150000;
const SLR_LIFETIME_LIMIT = 1200000;
const SLR_ANNUAL_USAGE = 80000;
const SLR_AVAILABLE = Math.max(
  0,
  Math.min(SLR_ANNUAL_LIMIT - SLR_ANNUAL_USAGE, SLR_LIFETIME_LIMIT - SLR_ANNUAL_USAGE)
);
const slrLimitDetail = {
  annual_limit: SLR_ANNUAL_LIMIT,
  annual_usage: SLR_ANNUAL_USAGE,
  available: SLR_AVAILABLE,
  balance_multiplier: 1,
  hard: true,
  limit: SLR_LIFETIME_LIMIT,
  one_month_limit: null,
  one_month_usage: 0,
  six_month_limit: null,
  six_month_usage: SLR_ANNUAL_USAGE,
  taxable_limit: null,
  three_month_limit: null,
  three_month_usage: SLR_ANNUAL_USAGE,
  usage: SLR_ANNUAL_USAGE,
  external_annual_usage: 0,
};
// No top-level external_annual_usage: the live wire has never carried it
// (only details.* do), so the fixture matches the serializer's attribute set.
const SLR_LIMITS = limitsStatusContract2Document([
  {
    annual_start_date: '2026-01-01',
    annual_end_date: '2026-12-31',
    as_of_date: '2026-06-05',
    available: SLR_AVAILABLE,
    available_with_override: SLR_AVAILABLE,
    details: {
      company: slrLimitDetail,
      product: slrLimitDetail,
      batch: slrLimitDetail,
      scheme: slrLimitDetail,
    },
    employee_id: 'demo-slr-employee-1',
    hard_limit: 'scheme_annual',
    limit_capacity: 'TAS.LimitCapacity.MONETARY',
    product: 'SLR',
    scheme: 'demo-slr-plan-1',
    soft_limit: 'scheme_annual',
  },
]);

// GET /employees/{id}/accounting_payments → past employer-contribution payments
// + a summary. `summary.total` (cents) MUST be defined: fetchEmployerContributions
// gates `isLiveData` on it and otherwise falls back to the career-pathways demo
// snapshot. The displayed YTD/lifetime are recomputed from the payment-history
// timeline, so an empty `payments` here is fine (the timeline is the source of truth).
const ACCOUNTING_PAYMENTS = { payments: [], summary: { total: 60000 } };

// ELF/LRAP enrollment status (SLR dashboard tile + wizard route guard). Demo
// personas are LRAP-eligible but not yet enrolled, so the ELF tile renders in
// its blank state — parity with the pre-tile CTA that always showed in demos.
// Without this matcher the demo fetch seam serves its blocked-response body,
// which the lrap-enrollment service rejects as malformed (fail-open to null).
const LRAP_ENROLLMENT_STATUS = {
  lrapEligible: true,
  status: 'not_started',
  employeePlanId: 1,
  enrolledAt: null,
  enrollmentStatus: null,
};

export function buildContributionsFetchMatchers(): FetchMatcher[] {
  const debt = getCurrentDemoDebtLevel();
  return [
    { urlIncludes: '/accounting_preview', json: FUTURE_PAYMENTS },
    { urlIncludes: '/accounting_payments', json: ACCOUNTING_PAYMENTS },
    { urlIncludes: '/slr-alerts', json: buildSlrAlerts(debt) },
    { urlIncludes: '/lrap-enrollments/status', json: LRAP_ENROLLMENT_STATUS },
    {
      urlMatches: (url) => url.includes('/api/v2/limits/status') && url.includes('product=SLR'),
      json: SLR_LIMITS,
    },
    // The contribution surfaces read the kit's payment-history endpoint
    // through a BUILDER (no SkipCache), so it reaches the fetch seam. The
    // document already carries the meta.page and meta.summary the kit serves.
    {
      urlIncludes: '/api/v2/employee-payment-histories',
      method: 'GET',
      json: contributionsCollection,
    },
  ];
}
